<template>
  <tr class="odd:bg-white even:bg-gray-100 child:p-3 text-left text-xs">
    <td>
      <div class="flex space-x-2 items-center">
        <button
          type="button"
          class="btn-icon btn-icon-red"
          @click="$emit('packageTradeItemRemoved')"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
        </button>

        <p class="w-60 text-wrap">
          {{ selectedTradeItem.name }}
        </p>
      </div>
    </td>

    <td>
      <div class="flex items-center space-x-2">
        <AppTypeAhead
          v-model="wbsCodeSearch"
          class="form-input p-1 text-xs"
          name="wbs_code"
          :suggestion-callback="getSuggestions"
          :initial-suggestions="getSuggestions"
          show-empty-suggestions
          :scrollable-container="scrollableContainer"
          @value-was-selected="assignWbsCode"
        >
          <template #emptyText>
            <p class="text-sm text-gray-500">
              No WBS codes match.
            </p>
          </template>
        </AppTypeAhead>
      </div>
    </td>

    <td class="text-right">
      {{ australianNumber(selectedTradeItem.quantity) }}
      ({{ selectedTradeItem.unit }})
    </td>

    <td class="text-right">
      {{ australianCurrency(selectedTradeItem.rate) }}
    </td>

    <td class="text-right">
      {{ australianCurrency(selectedTradeItem.budget) }}
    </td>

    <td class="text-right">
      <div class="flex justify-end items-center space-x-1">
        <AppInput
          v-model="packageTradeItemForm.form.awarded_works"
          name="awarded_works"
          type="number"
          min="0"
          max="100"
          step="0.01"
          class="form-input w-20 p-0 text-right"
          @blur="createPackageTradeItem"
          @keyup.enter="$event.target.blur()"
        />

        <span class="text-xs">%</span>
      </div>
    </td>

    <td
      class="text-right"
    >
      <div
        v-if="falsey(selectedPackage.procurement_method) || selectedPackage.procurement_method === 'schedule_of_rates'"
        class="flex justify-end items-center space-x-1"
      >
        <AppInput
          ref="quantityInput"
          v-model="packageTradeItemForm.form.quantity"
          name="quantity"
          type="number"
          class="form-input w-16 p-0"
          @blur="createPackageTradeItem"
          @keyup.enter="$event.target.blur()"
        />

        <span
          v-if="selectedTradeItem"
          class="text-xs"
        >
          ({{ selectedTradeItem.unit }})
        </span>
      </div>
    </td>

    <td>
      <AppCurrencyInput
        v-if="falsey(selectedPackage.procurement_method) || selectedPackage.procurement_method === 'schedule_of_rates'"
        v-model="packageTradeItemForm.form.rate"
        name="rate"
        class="ml-auto form-input w-16 p-0"
        @blur="createPackageTradeItem"
        @keyup.enter="$event.target.blur()"
      />
    </td>

    <td class="text-right">
      <AppCurrencyInput
        v-if="falsey(selectedPackage.procurement_method) || selectedPackage.procurement_method === 'lump_sum'"
        ref="valueInput"
        v-model="packageTradeItemForm.form.value"
        name="value"
        :disabled="packageTradeItemForm.form.rate > 0 && packageTradeItemForm.form.quantity > 0"
        class=" disabled:bg-gray-300 form-input w-16 p-0 ml-auto"
        @blur="createPackageTradeItem"
        @keyup.enter="$event.target.blur()"
      />

      <span
        v-else
        class="text-right"
      >
        {{ australianCurrency(packageTradeItemForm.form.value || 0) }}
      </span>
    </td>

    <td class="text-right">
      <button
        v-if="selectedPackage.procurement_method"
        type="button"
        class="btn-icon-green btn-icon"
        :disabled="!canSubmitForm"
        @click="createPackageTradeItem"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
      </button>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey } from '@/helpers';
import Package from '@/models/Package';
import TradeItem from '@/models/TradeItem';
import WbsCode from '@/models/WbsCode';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

const props = defineProps<{
  selectedTradeItem: TradeItem;
  selectedPackage: Package;
  scrollableContainer: HTMLElement;
}>();

const emits = defineEmits<{
  (e: 'packageTradeItemCreated', data: { addedVia: 'rate' | 'value' });
  (e: 'packageTradeItemRemoved');
}>();

const packageTradeItemsStore = usePackageTradeItemsStore();
const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const packageTradeItemForm = useFormData({
  rate: undefined,
  quantity: undefined,
  value: undefined,
  trade_item_id: props.selectedTradeItem.id,
  unit: '',
  name: '',
  wbs_code_id: undefined,
  awarded_works: 100,
});

watch(() => props.selectedPackage.procurement_method, (newValue) => {
  if(!newValue) {
    return;
  }

  packageTradeItemForm.form.awarded_works = 100;

  if(newValue === 'schedule_of_rates') {
    packageTradeItemForm.form.quantity = props.selectedTradeItem.quantity;
    packageTradeItemForm.form.rate = props.selectedTradeItem.rate;
  }

  if(newValue === 'lump_sum') {
    packageTradeItemForm.form.value = props.selectedTradeItem.budget;
  }
}, { immediate: true });

const createPackageTradeItemAction = useStoreApiAction(packageTradeItemsStore.create);

const canSubmitForm = computed(() => {
  return ((packageTradeItemForm.form.rate && packageTradeItemForm.form.quantity) || packageTradeItemForm.form.value) &&
    packageTradeItemForm.form.awarded_works;
});

watch(() => [packageTradeItemForm.form.rate, packageTradeItemForm.form.quantity], (newValue) => {
  if(newValue[0] > 0 && newValue[1] > 0) {
    packageTradeItemForm.form.value = newValue[0] * newValue[1];
  }
});

const createPackageTradeItem = () => {
  if(canSubmitForm.value) {
    createPackageTradeItemAction.request(project.value.id, props.selectedPackage.id, packageTradeItemForm.form)
      .then((packageTradeItem) => {
        const rateUsed = !!packageTradeItemForm.form.rate;

        packageTradeItemForm.resetData();
        emits('packageTradeItemCreated', { addedVia: rateUsed ? 'rate' : 'value' });
      }).catch((error) => {
        if(error?.status === 422) {
          Swal.fire({
            icon: 'error',
            title: 'Failed to save.',
            text: error?.data?.message,
          });
        }
      });
  }
};

const quantityInput = useTemplateRef('quantityInput');
const valueInput = useTemplateRef('valueInput');

const focusQuantity = () => {
  quantityInput.value.focusInput();
};

const focusValue = () => {
  valueInput.value.focusInput();
};

const wbsCodesStore = useWbsCodesStore();
const wbsCodeSearch = ref('');

const filteredWbsCodes = computed(() => {
  const searchTerm = wbsCodeSearch.value.toLowerCase();

  let wbsCodes = wbsCodesStore.models
    .where('project_id', project.value.id);

  if(
    props.selectedPackage.procurement_method === 'schedule_of_rates' ||
    (packageTradeItemForm.form.rate && packageTradeItemForm.form.quantity)
  ) {
    wbsCodes = wbsCodes.where('unit', props.selectedTradeItem.unit);
  }

  if(searchTerm.trim()) {
    wbsCodes = wbsCodes
      .where((wbsCode) => {
        return wbsCode.code.toLowerCase().includes(searchTerm) ||
          wbsCode.description.toLowerCase().includes(searchTerm);
      });
  }

  return wbsCodes.get();
});

const getSuggestions = () => {
  return filteredWbsCodes.value.map((wbsCode: WbsCode) => ({
    label: `${wbsCode.code} - ${wbsCode.unit}`,
    value: wbsCode.id,
  }));
};

const getInitialSuggestions = () => {
  return filteredWbsCodes.value
    .map((wbsCode: WbsCode) => ({
      label: `${wbsCode.code} - ${wbsCode.unit}`,
      value: wbsCode.id,
    }));
};

const assignWbsCode = ({ index, value }: { index: number; value: { label: string; value: unknown } }) => {
  packageTradeItemForm.form.wbs_code_id = value.value as Id;
};

defineExpose({
  focusQuantity,
  focusValue,
});
</script>

<style scoped></style>
